// src/components/admin/LandingAnalyticsTab.tsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Skeleton,
  useTheme,
  alpha,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import ReactECharts from "echarts-for-react";
import { useTranslation } from "../../i18n";
import { fetchData } from "../../api/apiHelpers";
import useChartTheme from "../dashboards/charts/useChartTheme";
import {
  glass,
  glassTableSx,
  glassAxis,
  glassChartTooltip,
} from "../../theme/glassTokens";

interface AnalyticsData {
  period_days: number;
  total_views: number;
  unique_visitors: number;
  authenticated_views: number;
  signup_funnel: { landed: number; viewed_login: number };
  daily: { date: string; views: number; unique: number }[];
  top_pages: { path: string; views: number }[];
  by_country: { country: string; views: number }[];
  referrers: { referrer: string; views: number }[];
}

const countryNames = new Intl.DisplayNames(["en"], { type: "region" });
function getCountryName(code: string): string {
  if (!code || code === "Unknown") return "Unknown";
  try {
    return countryNames.of(code.toUpperCase()) || code;
  } catch {
    return code;
  }
}

const LandingAnalyticsTab: React.FC = () => {
  const theme = useTheme();
  const textPrimary = glass.textPrimary;
  const textSecondary = glass.textSecondary;
  const { t } = useTranslation();
  const chartTheme = useChartTheme();
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  useEffect(() => {
    setLoading(true);
    fetchData<AnalyticsData>(`/billing/admin/landing-analytics/?days=${days}`)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [days]);

  const dailyChartOption = data
    ? {
        tooltip: {
          ...glassChartTooltip(chartTheme.baseTooltip),
          trigger: "axis" as const,
        },
        grid: chartTheme.baseGrid,
        xAxis: {
          type: "category" as const,
          data: data.daily.map((d) => d.date?.slice(5) || ""),
          ...glassAxis(chartTheme.baseAxis),
        },
        yAxis: { type: "value" as const, ...glassAxis(chartTheme.baseAxis) },
        series: [
          {
            name: "Views",
            type: "line" as const,
            data: data.daily.map((d) => d.views),
            smooth: true,
            areaStyle: { color: alpha(glass.accent, 0.15) },
            lineStyle: { color: glass.accent, width: 2 },
            itemStyle: { color: glass.accent },
          },
          {
            name: "Daily Unique",
            type: "line" as const,
            data: data.daily.map((d) => d.unique),
            smooth: true,
            lineStyle: { color: theme.palette.secondary.main, width: 2, type: "dashed" as const },
            itemStyle: { color: theme.palette.secondary.main },
          },
        ],
      }
    : null;

  const funnelConversion =
    data && data.signup_funnel.landed > 0
      ? ((data.signup_funnel.viewed_login / data.signup_funnel.landed) * 100).toFixed(1)
      : "N/A";

  return (
    <Box>
      {/* Period selector */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 3 }}>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>{t("admin.period", "Period")}</InputLabel>
          <Select value={days} label={t("admin.period", "Period")} onChange={(e) => setDays(e.target.value as number)}>
            <MenuItem value={7}>{t("admin.7_days", "7 days")}</MenuItem>
            <MenuItem value={14}>{t("admin.14_days", "14 days")}</MenuItem>
            <MenuItem value={30}>{t("admin.30_days", "30 days")}</MenuItem>
            <MenuItem value={90}>{t("admin.90_days", "90 days")}</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Summary cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: t("admin.total_views", "Total Views"), value: data?.total_views ?? 0, color: glass.accent },
          { label: t("admin.unique_visitors", "Unique Visitors (total)"), value: data?.unique_visitors ?? 0, color: glass.accent },
          { label: t("admin.authenticated", "Authenticated"), value: data?.authenticated_views ?? 0, color: theme.palette.success.light },
          { label: t("admin.funnel_conversion", "Login Conversion"), value: `${funnelConversion}%`, color: theme.palette.warning.main },
        ].map((card, i) => (
          <Grid item xs={6} md={3} key={i}>
            <Box
              sx={{
                p: { xs: 1.5, sm: 2.5 },
                borderRadius: 2,
                background: glass.panel,
                border: `1px solid ${glass.panelBorder}`,
                backdropFilter: "blur(8px)",
                transition: "border-color 0.2s ease",
                "&:hover": { borderColor: glass.headerDivider },
                textAlign: "center",
              }}
            >
              <Typography variant="caption" sx={{ color: textSecondary }}>{card.label}</Typography>
              {loading ? (
                <Skeleton width={60} height={36} sx={{ mx: "auto" }} />
              ) : (
                <Typography variant="h5" fontWeight={700} sx={{ color: card.color }}>
                  {typeof card.value === "number" ? card.value.toLocaleString() : card.value}
                </Typography>
              )}
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* Daily visitors chart */}
      <Box
        sx={{
          p: { xs: 1.5, sm: 2.5 },
          borderRadius: 2,
          background: glass.panel,
          border: `1px solid ${glass.panelBorder}`,
          backdropFilter: "blur(8px)",
          transition: "border-color 0.2s ease",
          "&:hover": { borderColor: glass.headerDivider },
          mb: 3,
        }}
      >
        <Typography variant="subtitle2" fontWeight={700} gutterBottom sx={{ color: textPrimary }}>
          {t("admin.daily_visitors", "Daily Visitors")}
        </Typography>
        {loading ? (
          <Skeleton variant="rectangular" height={chartTheme.chartHeight} />
        ) : dailyChartOption ? (
          <ReactECharts option={dailyChartOption} style={{ height: chartTheme.chartHeight }} />
        ) : (
          <Typography sx={{ color: textSecondary, py: 4, textAlign: "center" }}>
            No data available
          </Typography>
        )}
      </Box>

      {/* Two-column: Top Pages + Countries */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              p: { xs: 1.5, sm: 2.5 },
              borderRadius: 2,
              background: glass.panel,
              border: `1px solid ${glass.panelBorder}`,
              backdropFilter: "blur(8px)",
              transition: "border-color 0.2s ease",
              "&:hover": { borderColor: glass.headerDivider },
              height: "100%",
            }}
          >
            <Typography variant="subtitle2" fontWeight={700} gutterBottom sx={{ color: textPrimary }}>
              {t("admin.top_pages", "Top Pages")}
            </Typography>
            <TableContainer>
              <Table size="small" sx={glassTableSx}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem" }}>{t("admin.col_path", "Path")}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.75rem" }}>{t("admin.col_views", "Views")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading
                    ? Array.from({ length: 5 }).map((_, i) => (
                        <TableRow key={i}>
                          <TableCell><Skeleton /></TableCell>
                          <TableCell><Skeleton /></TableCell>
                        </TableRow>
                      ))
                    : data?.top_pages.map((p, i) => (
                        <TableRow key={i} hover>
                          <TableCell sx={{ fontSize: "0.8rem" }}>{p.path}</TableCell>
                          <TableCell align="right" sx={{ fontSize: "0.8rem", fontWeight: 600 }}>
                            {p.views.toLocaleString()}
                          </TableCell>
                        </TableRow>
                      ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              p: { xs: 1.5, sm: 2.5 },
              borderRadius: 2,
              background: glass.panel,
              border: `1px solid ${glass.panelBorder}`,
              backdropFilter: "blur(8px)",
              transition: "border-color 0.2s ease",
              "&:hover": { borderColor: glass.headerDivider },
              height: "100%",
            }}
          >
            <Typography variant="subtitle2" fontWeight={700} gutterBottom sx={{ color: textPrimary }}>
              {t("admin.by_country", "Traffic by Country")}
            </Typography>
            <TableContainer>
              <Table size="small" sx={glassTableSx}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem" }}>{t("admin.col_country", "Country")}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.75rem" }}>{t("admin.col_views", "Views")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading
                    ? Array.from({ length: 5 }).map((_, i) => (
                        <TableRow key={i}>
                          <TableCell><Skeleton /></TableCell>
                          <TableCell><Skeleton /></TableCell>
                        </TableRow>
                      ))
                    : data?.by_country.map((c, i) => (
                        <TableRow key={i} hover>
                          <TableCell sx={{ fontSize: "0.8rem" }}>{getCountryName(c.country)}</TableCell>
                          <TableCell align="right" sx={{ fontSize: "0.8rem", fontWeight: 600 }}>
                            {c.views.toLocaleString()}
                          </TableCell>
                        </TableRow>
                      ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Grid>
      </Grid>

      {/* Referrers */}
      {data && data.referrers.length > 0 && (
        <Box
          sx={{
            p: { xs: 1.5, sm: 2.5 },
            borderRadius: 2,
            background: glass.panel,
            border: `1px solid ${glass.panelBorder}`,
            backdropFilter: "blur(8px)",
            transition: "border-color 0.2s ease",
            "&:hover": { borderColor: glass.headerDivider },
            mt: 2,
          }}
        >
          <Typography variant="subtitle2" fontWeight={700} gutterBottom sx={{ color: textPrimary }}>
            {t("admin.referrers", "Top Referrers")}
          </Typography>
          <TableContainer>
            <Table size="small" sx={glassTableSx}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem" }}>{t("admin.col_referrer", "Referrer")}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: "0.75rem" }}>{t("admin.col_views", "Views")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.referrers.map((r, i) => (
                  <TableRow key={i} hover>
                    <TableCell sx={{ fontSize: "0.8rem", maxWidth: 400, overflow: "hidden", textOverflow: "ellipsis" }}>
                      {r.referrer}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: "0.8rem", fontWeight: 600 }}>
                      {r.views.toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
};

export default LandingAnalyticsTab;
